// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AccountMessages from "@insite/content-library/Widgets/MyAccount/AccountMessages";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import MiniMyAccount, { MiniMyAccountStyles } from "@werner/App/Components/MiniMyAccount/MiniMyAccount";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { getAuthenticatedHeaderSignInWidget } from "@werner/App/Store/Selectors/WidgetSelectors";
import { mapLinks, XMappedLink } from "@werner/Overrides/Widgets/Common/HeaderSignIn";
import { xScreen } from "@werner/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}
// XNG-Changes
const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyAccountViewStyles {
    quickLinksGridItem?: GridItemProps;
    mainSectionGridItem?: GridItemProps;
    mainSectionGridContainer?: GridContainerProps;
    accountMessagesGridItem?: GridItemProps;
    dashboardWidgetsGridItem?: GridItemProps;
    // XNG-Changes
    myAccountGridContainer?: GridContainerProps;
    miniMyAccountGridItem?: GridItemProps;
    miniMyAccountStyles?: MiniMyAccountStyles;
}

// XNG-Changes
const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const miniLinks = getAuthenticatedHeaderSignInWidget(state);
    let linkList: XMappedLink[] = [];
    let linksTitle = "";
    if (miniLinks) {
        linkList = mapLinks(state, miniLinks?.extraLinks);
        linksTitle = miniLinks?.navListTitle;
    }
    const myAccountPageLink = getPageLinkByPageType(state, "MyAccountPage");
    const filteredMyAccountLinks = myAccountPageLink?.children?.filter(l => !l.excludeFromNavigation);
    return {
        // XNG-Changes
        linksTitle,
        miniMyAccountFields: linkList.filter(l => filteredMyAccountLinks?.some(link => link.url === l.url)),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        isPunchOutSession: getIsPunchOutSession(state),
    };
};
// XNG-Changes
export const myAccountViewStyles: MyAccountViewStyles = {};

const styles = myAccountViewStyles;

class MyAccountView extends React.PureComponent<Props> {
    // XNG-Changes: add onSignOutHandler
    onSignOutHandler = () => {
        this.props.isPunchOutSession ? this.props.cancelPunchOut() : this.props.signOut();
    };

    render() {
        return (
            <GridContainer gap={45} {...styles.myAccountGridContainer}>
                {/* // XNG-Changes */}
                <GridItem {...styles.miniMyAccountGridItem}>
                    <MiniMyAccount
                        myAccountLinks={{ title: this.props.linksTitle, links: this.props.miniMyAccountFields }}
                        extendedStyles={styles.miniMyAccountStyles}
                        displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                        isPunchOutSession={this.props.isPunchOutSession}
                        onSignOutHandler={this.onSignOutHandler}
                    />
                </GridItem>
                <GridItem {...styles.quickLinksGridItem}>
                    <Zone contentId={this.props.id} zoneName="NavigationList" />
                </GridItem>
                <GridItem {...styles.mainSectionGridItem}>
                    <GridContainer {...styles.mainSectionGridContainer}>
                        <GridItem {...styles.accountMessagesGridItem}>
                            {createWidgetElement("MyAccount/MyAccountHomepageSelector", { fields: {} })}
                            <AccountMessages />
                        </GridItem>
                        <GridItem {...styles.dashboardWidgetsGridItem}>
                            <Zone contentId={this.props.id} zoneName="DashboardWidgets" />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        );
    }
}

const myAccountViewWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountView),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
    },
};

export default myAccountViewWidget;
